<!-- MAIA Step 4 of 4 — Group your locations / import. Design phase (mock data). -->

<!-- ============================ GROUPING VIEW ============================ -->
<div class="maia-group-step" *ngIf="view === 'grouping'" data-testid="maia-group-step">


    <header class="step-header">
        <div class="step-heading">
            <h1 *ngIf="!allGrouped">Group your locations</h1>
            <h1 *ngIf="allGrouped">All locations grouped</h1>
            <p *ngIf="!allGrouped">
                Select locations and add them to groups. You can create new groups or use existing ones.
                All locations must be grouped before continuing.
            </p>
            <p *ngIf="allGrouped">
                All {{ totalLocations }} locations have been assigned to groups. You're ready to continue
                to the final report and import to Maptycs.
            </p>
        </div>
    </header>

    <div class="step-body">

        <!-- Left: progress + filter + assign -->
        <section class="maia-panel assign-panel">
            <div class="progress-head">
                <span class="progress-count">{{ groupedLocations }} of {{ totalLocations }} locations grouped</span>
                <span class="progress-pct">{{ groupedPercent }}% Complete</span>
            </div>
            <div class="progress-track">
                <div class="progress-fill" [style.width.%]="groupedPercent"></div>
            </div>

            <h3 class="panel-subtitle">{{ allGrouped ? 'All locations grouped' : 'Filter your locations' }}</h3>

            <!-- Faceted filter: pick a field, tick its distinct values -->
            <div class="filter-row">
                <select class="facet-select" [(ngModel)]="selectedFieldId" [disabled]="allGrouped || selectAll"
                        (ngModelChange)="onFieldSelected()">
                    <option [ngValue]="null">Filter by field…</option>
                    <option *ngFor="let f of fields" [ngValue]="f.id">{{ f.displayName || f.name }}</option>
                </select>
                <span class="or-sep">or</span>
                <label class="select-all">
                    <input type="checkbox" [(ngModel)]="selectAll" [disabled]="allGrouped"
                           (ngModelChange)="onSelectAllChange()" />
                    <span>select all {{ remainingCount }} {{ groupedLocations > 0 ? 'remaining ' : '' }}locations</span>
                </label>
            </div>

            <!-- Value checklist (text) / min-max range (number) / from-to range (date) for the selected field -->
            <div class="value-picker" *ngIf="showValuePicker && !selectAll">
                <ng-container *ngIf="!selectedFieldIsNumber && !selectedFieldIsDate">
                    <div class="value-search">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                        <input type="text" [(ngModel)]="valueSearch" placeholder="Search values…" />
                    </div>
                    <p class="value-empty" *ngIf="!loadingValues && filteredValueOptions.length === 0">No values</p>
                    <ul class="value-list">
                        <li *ngFor="let v of filteredValueOptions">
                            <label>
                                <input type="checkbox" [(ngModel)]="valuePicks[v]" />
                                <span>{{ v }}</span>
                            </label>
                        </li>
                    </ul>
                </ng-container>

                <div class="range-inputs" *ngIf="selectedFieldIsNumber">
                    <label>
                        <span>Min</span>
                        <div class="range-field">
                            <input type="number" [(ngModel)]="rangeMin"
                                   (focus)="focusedRange = 'min'" (blur)="focusedRange = null" />
                            <span class="range-tip" *ngIf="focusedRange === 'min' && dataMin != null">
                                Statistical Minimum: {{ dataMin | number }}
                            </span>
                        </div>
                    </label>
                    <label>
                        <span>Max</span>
                        <div class="range-field">
                            <input type="number" [(ngModel)]="rangeMax"
                                   (focus)="focusedRange = 'max'" (blur)="focusedRange = null" />
                            <span class="range-tip" *ngIf="focusedRange === 'max' && dataMax != null">
                                Statistical Maximum: {{ dataMax | number }}
                            </span>
                        </div>
                    </label>
                </div>

                <div class="range-inputs" *ngIf="selectedFieldIsDate">
                    <label>
                        <span>From</span>
                        <div class="range-field">
                            <input type="date" [(ngModel)]="dateFrom" [class.is-empty]="!dateFrom"
                                   [min]="dataMinDate" [max]="dateTo || dataMaxDate"
                                   (click)="openDatePicker($event)" />
                        </div>
                    </label>
                    <label>
                        <span>To</span>
                        <div class="range-field">
                            <input type="date" [(ngModel)]="dateTo" [class.is-empty]="!dateTo"
                                   [min]="dateFrom || dataMinDate" [max]="dataMaxDate"
                                   (click)="openDatePicker($event)" />
                        </div>
                    </label>
                </div>

                <div class="value-actions">
                    <button type="button" class="btn btn-add" [disabled]="!canApply" (click)="applyCriterion()">
                        Apply
                    </button>
                    <button type="button" class="btn btn-ghost" (click)="showValuePicker = false">Cancel</button>
                </div>
            </div>

            <p class="value-loading" *ngIf="loadingValues">Loading values…</p>

            <!-- Applied criteria -->
            <div class="criteria-chips" *ngIf="criteria.length && !selectAll">
                <span class="chip" *ngFor="let c of criteria">
                    <span class="chip-text">{{ c.fieldName }}: {{ c.label }}</span>
                    <button type="button" class="chip-remove" (click)="removeCriterion(c.fieldId)"
                            aria-label="Remove filter">×</button>
                </span>
            </div>

            <span class="match-chip">
                <ng-container *ngIf="loadingMatch">Counting…</ng-container>
                <ng-container *ngIf="!loadingMatch">
                    {{ matchCount > 0 ? matchCount + ' locations match' : 'No locations selected' }}
                </ng-container>
            </span>

            <div class="add-row">
                <span class="add-label">Add to:</span>
                <select class="group-select" [(ngModel)]="selectedGroupId" [disabled]="allGrouped">
                    <option [ngValue]="null">Select a group…</option>
                    <option *ngFor="let g of groups" [ngValue]="g.id">{{ g.name }}</option>
                </select>
                <button type="button" class="btn btn-add" [disabled]="!canAdd" (click)="addToGroup()">
                    Add to Group
                </button>
            </div>

            <a class="create-link" [class.disabled]="allGrouped" (click)="!allGrouped && openCreateGroupAndAdd()">
                or + Create new group and add
            </a>
        </section>

        <!-- Right: existing groups -->
        <section class="maia-panel groups-panel">
            <div class="groups-head">
                <span class="groups-title">EXISTING GROUPS</span>
                <a class="new-group-link" (click)="openCreateGroup()">+ New Group</a>
            </div>

            <div class="create-group-form" *ngIf="creatingGroup">
                <input type="text" class="create-group-input" [(ngModel)]="newGroupName"
                       placeholder="New group name" (keyup.enter)="confirmCreateGroup()" />
                <label class="create-group-label">Parent group</label>
                <select class="create-group-select" [(ngModel)]="newGroupParentId">
                    <option *ngFor="let g of groups" [ngValue]="g.id">{{ g.name }}</option>
                </select>
                <div class="create-group-actions">
                    <button type="button" class="btn btn-add" [disabled]="!newGroupName.trim() || savingGroup"
                            (click)="confirmCreateGroup()">Create</button>
                    <button type="button" class="btn btn-ghost" (click)="cancelCreateGroup()">Cancel</button>
                </div>
            </div>

            <p class="groups-status" *ngIf="loadingGroups">Loading groups…</p>
            <p class="groups-status" *ngIf="!loadingGroups && displayGroups.length === 0">
                No groups yet — create one to get started.
            </p>

            <ul class="group-list" *ngIf="!loadingGroups && displayGroups.length">
                <li class="group-row" *ngFor="let g of displayGroups" [class.subgroup]="g.isSub"
                    [style.margin-left.px]="g.depth * 18">
                    <button *ngIf="g.hasChildren" type="button" class="group-toggle" (click)="toggleGroup(g.id)"
                            [attr.aria-expanded]="g.expanded" aria-label="Toggle subgroups">
                        <svg class="group-chevron" [class.expanded]="g.expanded" width="14" height="14"
                             viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                             stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <path d="M9 6l6 6-6 6"></path>
                        </svg>
                    </button>
                    <span *ngIf="!g.hasChildren" class="group-toggle-spacer" aria-hidden="true"></span>
                    <svg class="group-folder" [class.sub]="g.isSub" width="18" height="18" viewBox="0 0 24 24"
                         fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"
                         stroke-linejoin="round" aria-hidden="true">
                        <path d="M3 7a2 2 0 0 1 2-2h3.5l2 2H19a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <ng-container *ngIf="g.isSub">
                            <path d="M8.5 13.5h5"></path>
                            <path d="M12 11.5l2 2-2 2"></path>
                        </ng-container>
                    </svg>
                    <span class="group-name">{{ g.name }}</span>
                    <span class="group-count" *ngIf="!g.hasChildren" [class.has-data]="g.count > 0">
                        {{ g.count }} locations
                    </span>
                </li>
            </ul>
        </section>

    </div>

    <p class="import-error" *ngIf="importError">{{ importError }}</p>

    <footer class="step-footer">
        <button type="button" class="btn btn-secondary" [disabled]="importing" (click)="backRequested.emit()">
            Back
        </button>
        <button type="button" class="btn btn-primary" [disabled]="!allGrouped || importing"
                (click)="importPortfolio()">
            {{ importing ? 'Importing…' : 'Import to Maptycs' }}
        </button>
    </footer>
</div>

<!-- ============================ SUCCESS VIEW ============================= -->
<div class="maia-group-step imported-view" *ngIf="view === 'imported'" data-testid="maia-import-success">

    <header class="step-header">
        <div class="step-heading">
            <h1>Portfolio imported successfully</h1>
            <p>
                Your portfolio with {{ importedLocations }} locations has been imported to Maptycs.
                You can now explore your data on the map or download the enriched portfolio.
            </p>
        </div>
    </header>

    <div class="success-card">
        <div class="success-check">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
        </div>
        <div class="success-title">{{ importedLocations }} locations imported to Maptycs</div>
        <div class="success-sub">{{ importedGroups }} groups · {{ importedRegions }} regions · Geocoded &amp; enriched</div>
        <div class="success-warning" *ngIf="failedGeocodeCount > 0">
            {{ failedGeocodeCount }} locations failed geocoding due to Missing or Incorrect data for Country, Address, City, Zip Code.
        </div>
    </div>

    <footer class="step-footer center">
        <button type="button" class="btn btn-secondary" (click)="downloadRequested.emit()">
            Download Portfolio ↓
        </button>
        <button type="button" class="btn btn-primary" (click)="mapRequested.emit()">
            Go to Map View →
        </button>
    </footer>
</div>
